import { useEffect } from 'react';

import { BackButton } from '../../../../../../components/Clickables/Controls/BackButton/BackButton';
import { Heading } from '../../../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { RegulationCards } from '../../../../../../components/RegulationCards/RegulationCards';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useRegulationBoundaryAtLocation } from '@domain/regulations/hooks/useRegulationBoundaryAtLocation';
import { useSpeciesRegulationAtLocation } from '@domain/regulations/hooks/useSpeciesRegulationAtLocation';
import { useSpeciesById } from '@domain/regulations/hooks/useSpeciesById';
import { useMapLocation } from '@domain/map/useMapLocation';
import { getAdditionalInformation } from '@domain/regulations/getAdditionalInformation';
import { isProhibitedLocation } from '@domain/regulations/isProhibitedLocation';
import { useExplore } from '../../../../context/ExploreContext';
import { getWaterType } from '../../helpers';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';
import { SingleSpeciesHeader } from './components/SingleSpeciesHeader';

interface IProps {
  fishrulesSpeciesId: number;
}

export const RegulationView = ({ fishrulesSpeciesId }: IProps) => {
  const mapLocation = useMapLocation();
  const { saveParamsInUrl } = useExplore();
  const coordinates = mapLocation?.coordinates;

  const { isLoading: isLoadingSpecies, regulation } = useSpeciesRegulationAtLocation(
    coordinates ? { fishrulesSpeciesId, ...coordinates } : undefined,
  );

  const { isLoading: isLoadingSingleSpecies, species: singleSpecies } = useSpeciesById({
    fishrulesSpeciesId,
  });

  const { isLoading: isLoadingBoundaries, boundaries } =
    useRegulationBoundaryAtLocation(coordinates);

  const boundaryName = boundaries && boundaries.length > 0 ? boundaries[0]?.name : '';

  useEffect(() => {
    if (regulation?.id && boundaryName !== '') {
      trackEvent({
        name: 'mykiss_explore_regulation_viewed',
        properties: {
          regulation_id: regulation.id.toString(),
          specie_id: regulation.fish_id.toString(),
          species_name: regulation.species,
          boundaries: boundaryName ?? '',
          water_type: getWaterType({
            saltwater: regulation.saltwater,
            freshwater: regulation.freshwater,
          }),
        },
      });
    }
  }, [
    regulation?.id,
    regulation?.fish_id,
    regulation?.species,
    regulation?.saltwater,
    regulation?.freshwater,
    boundaryName,
  ]);

  const isLoading = isLoadingSpecies || isLoadingBoundaries;

  if (isLoading || isLoadingSingleSpecies) {
    return (
      <MapDrawerLayout
        headerContent={
          <BackButton
            onClick={() => {
              saveParamsInUrl({ drawerView: 'regulations', drawerViewId: undefined });
            }}
            variant="controlLakeFogBg"
          />
        }
        withCloseButton={false}
      >
        <LoadingSpinner loadingText="Loading regulations in the area..." className="mt-6" />
      </MapDrawerLayout>
    );
  }

  if (!regulation || !boundaries || boundaries.length === 0) {
    return (
      <MapDrawerLayout withCloseButton={false}>
        <SingleSpeciesHeader
          speciesName={singleSpecies?.species ?? 'the selected species'}
          coordinates={coordinates}
          boundaries={boundaries}
        />
        <MapDrawerNotification
          className="mt-[100px]"
          title={`No regulations for ${
            singleSpecies?.species ?? 'the selected species'
          } in this area`}
          description="Try another area or species to get more results"
        />
      </MapDrawerLayout>
    );
  }

  const { isProhibited, message } = isProhibitedLocation(boundaries);

  const additionalInformation = getAdditionalInformation({ regulation, boundaries });

  return (
    <MapDrawerLayout withCloseButton={false}>
      <SingleSpeciesHeader
        speciesName={regulation.species}
        coordinates={coordinates}
        boundaries={boundaries}
      />
      <div className="mb-10 grid grid-cols-2 gap-2">
        <RegulationCards
          coordinates={coordinates!}
          regulation={regulation}
          infoCardSize="sm"
          isProhibitedLocation={isProhibited}
          prohibitedMessage={message}
        />
      </div>
      {additionalInformation.map(
        ({ order, visible, title, body }) =>
          visible && (
            <div className="mb-10" key={order}>
              <Heading variant="2xs" className="mb-3">
                {title}
              </Heading>
              {body}
            </div>
          ),
      )}
    </MapDrawerLayout>
  );
};
